import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Eye, Loader2, ScrollText } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { formatDateID } from "../lib/format";
import { PaginationBar, tdClass, thClass } from "../components/Common";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../components/ui/dialog";

export default function AuditTrail() {
  const [items, setItems] = useState([]);
  const [modules, setModules] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [limit, setLimit] = useState(25);
  const [module, setModule] = useState("");
  const [q, setQ] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        module, q, date_from: dateFrom, date_to: dateTo,
        page: String(page), limit: String(limit),
      });
      const { data } = await api.get(`/audit-logs?${params.toString()}`);
      setItems(data.items);
      setTotal(data.total);
      setPages(data.pages);
      setModules(data.modules || []);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [module, q, dateFrom, dateTo, page, limit]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const fmtTime = (iso) => {
    if (!iso) return "-";
    return `${formatDateID(iso.slice(0, 10))} ${iso.slice(11, 19)}`;
  };

  const inputCls = "rounded-lg border border-slate-700 bg-[#1E293B] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600";

  return (
    <div className="space-y-4" data-testid="audit-page">
      <div className="flex items-center gap-2">
        <ScrollText className="w-5 h-5 text-slate-200" />
        <div>
          <h2 className="text-xl font-bold tracking-tight text-slate-100">Audit Trail</h2>
          <p className="text-xs text-slate-400">Seluruh aktivitas penting tercatat dan tidak dapat dihapus</p>
        </div>
      </div>

      <div className="bg-[#1E293B] border border-slate-800 rounded-xl shadow-sm p-4 flex flex-wrap items-center gap-2">
        <input data-testid="input-audit-search" className={`${inputCls} w-56`} placeholder="Cari aktivitas / user…" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} />
        <select data-testid="select-audit-module" className={inputCls} value={module} onChange={(e) => { setModule(e.target.value); setPage(1); }}>
          <option value="">Semua Modul</option>
          {modules.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
        <input data-testid="input-audit-date-from" type="date" className={inputCls} value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPage(1); }} />
        <span className="text-xs text-slate-400">s/d</span>
        <input data-testid="input-audit-date-to" type="date" className={inputCls} value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPage(1); }} />
      </div>

      <div className="bg-[#1E293B] border border-slate-800 rounded-xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-7 h-7 animate-spin text-slate-400" /></div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table data-testid="table-audit" className="w-full border-collapse text-sm text-left">
                <thead>
                  <tr>
                    <th className={thClass}>Waktu</th>
                    <th className={thClass}>User</th>
                    <th className={thClass}>Aktivitas</th>
                    <th className={thClass}>Modul</th>
                    <th className={thClass}>IP</th>
                    <th className={`${thClass} text-center`}>Detail</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((a) => (
                    <tr key={a.id} className="hover:bg-slate-800/40" data-testid={`row-audit-${a.id}`}>
                      <td className={`${tdClass} whitespace-nowrap font-num text-xs`}>{fmtTime(a.created_at)}</td>
                      <td className={`${tdClass} font-medium text-slate-100`}>{a.user_name}</td>
                      <td className={tdClass}>{a.action}</td>
                      <td className={tdClass}>
                        <span className="bg-slate-800 text-slate-200 rounded-full px-2.5 py-0.5 text-xs font-medium">{a.module}</span>
                      </td>
                      <td className={`${tdClass} font-num text-xs text-slate-400`}>{a.ip || "-"}</td>
                      <td className={`${tdClass} text-center`}>
                        {(a.old_value || a.new_value) ? (
                          <button data-testid={`btn-audit-detail-${a.id}`} onClick={() => setDetail(a)} className="p-1.5 rounded-md hover:bg-slate-800/40 text-slate-400 hover:text-slate-100"><Eye className="w-4 h-4" /></button>
                        ) : <span className="text-slate-400">—</span>}
                      </td>
                    </tr>
                  ))}
                  {items.length === 0 && (
                    <tr><td colSpan={6} className="p-10 text-center text-slate-400 text-sm">Belum ada aktivitas tercatat</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <PaginationBar page={page} pages={pages} total={total} onPage={setPage} limit={limit} onLimit={(n) => { setLimit(n); setPage(1); }} />
          </>
        )}
      </div>

      <Dialog open={Boolean(detail)} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent data-testid="modal-audit-detail" className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Detail Aktivitas</DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-3 text-sm">
              <div className="rounded-lg border border-slate-800 divide-y divide-slate-800">
                {[["Waktu", fmtTime(detail.created_at)], ["User", detail.user_name],
                  ["Aktivitas", detail.action], ["Modul", detail.module],
                  ["ID Data", detail.record_id || "-"], ["IP", detail.ip || "-"]].map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-4 px-3 py-2">
                    <span className="text-slate-400">{k}</span>
                    <span className="font-medium text-slate-100 text-right break-all">{v}</span>
                  </div>
                ))}
              </div>
              {detail.old_value && (
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-rose-400 mb-1">Sebelum</p>
                  <pre className="rounded-lg bg-rose-500/15 border border-rose-500/30 p-3 text-xs text-slate-200 overflow-x-auto">{JSON.stringify(detail.old_value, null, 2)}</pre>
                </div>
              )}
              {detail.new_value && (
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-emerald-400 mb-1">Sesudah</p>
                  <pre className="rounded-lg bg-emerald-500/15 border border-emerald-500/30 p-3 text-xs text-slate-200 overflow-x-auto">{JSON.stringify(detail.new_value, null, 2)}</pre>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
